import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { boolean, index, pgTable, text, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { organization, user } from '../../auth'
import { Lesson } from '../../content'
import { LearnEnrollment } from '../learn-enrollment'
import { EarnedXp } from './earned-xp'
import { QuizAttempt } from './quiz-attempt'

export const LearnProgress = pgTable(
  'learn_progress',
  {
    id: uuid().primaryKey().defaultRandom(),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    organizationId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'cascade' }),
    lessonId: uuid()
      .notNull()
      .references(() => Lesson.id, { onDelete: 'cascade' }),
    learnEnrollmentId: uuid()
      .notNull()
      .references(() => LearnEnrollment.id, { onDelete: 'cascade' }),
    startedAt: timestamp({ mode: 'date', withTimezone: true }),
    endedAt: timestamp({ mode: 'date', withTimezone: true }),
    status: text({ enum: ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED'] })
      .notNull()
      .default('NOT_STARTED'),
    gradedByUserId: text().references(() => user.id, { onDelete: 'cascade' }),
    gradedAt: timestamp({ mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),

    // DEAD FLAGS — all three pairs below fed the deleted per-table producers. Nothing reads them: the
    // daily lookup tables are recomputed from these rows by `compute<Metric>`
    // (packages/trigger/src/metrics/), a mutation that changes a past day calls `recomputePastDays`,
    // and "Lanjut Belajar" is read live. Kept until a migration drops them.

    // Lesson metrics daily tracking (lookup table aggregation)
    calculatedForLessonMetricsDaily: boolean().default(false).notNull(),
    lessonMetricsDailyCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),

    // Lesson subject metrics daily tracking (per-subject grade lookup table).
    calculatedForSubjectMetricsDaily: boolean().default(false).notNull(),
    subjectMetricsDailyCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),

    // Continue lesson tracking (background task processing)
    calculatedForContinueLesson: boolean().default(false).notNull(),
    continueLessonCalculatedAt: timestamp({ mode: 'date', withTimezone: true }),
  },
  (t) => [
    index('idx_learn_progress_user').on(t.userId),
    // Live continue-lesson and last-activity reads: continueLessonLive, findLessonLastActivity.
    index('idx_learn_progress_user_updated').on(t.userId, t.updatedAt.desc()),
    index('idx_learn_progress_lesson_metrics_daily').on(t.calculatedForLessonMetricsDaily),
    index('idx_learn_progress_subject_metrics_daily').on(t.calculatedForSubjectMetricsDaily),
    index('idx_learn_progress_continue_lesson').on(t.calculatedForContinueLesson),
    index('idx_learn_progress_enrollment').on(t.learnEnrollmentId),
    index('idx_learn_progress_lesson').on(t.lessonId),
    index('idx_learn_progress_user_status').on(t.userId, t.status),
    uniqueIndex('unq_learn_progress_lesson_enrollment').on(t.lessonId, t.learnEnrollmentId),
  ],
)

export const learnProgressRelations = relations(LearnProgress, ({ one, many }) => ({
  user: one(user, {
    fields: [LearnProgress.userId],
    references: [user.id],
  }),

  organization: one(organization, {
    fields: [LearnProgress.organizationId],
    references: [organization.id],
  }),

  lesson: one(Lesson, {
    fields: [LearnProgress.lessonId],
    references: [Lesson.id],
  }),

  learnEnrollment: one(LearnEnrollment, {
    fields: [LearnProgress.learnEnrollmentId],
    references: [LearnEnrollment.id],
  }),

  gradedByUser: one(user, {
    fields: [LearnProgress.gradedByUserId],
    references: [user.id],
  }),

  earnedXps: many(EarnedXp),
  quizAttempts: many(QuizAttempt),
}))

export const InsertLearnProgressSchema = createInsertSchema(LearnProgress)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    gradedAt: true,
    gradedByUserId: true,
  })
  .superRefine((data, ctx) => {
    if (data.startedAt && data.endedAt && data.startedAt > data.endedAt) {
      ctx.addIssue({
        code: 'custom',
        message: 'Started at must be before ended at',
        path: ['startedAt'],
      })
    }
  })

export const UpdateLearnProgressSchema = createUpdateSchema(LearnProgress)
  .partial()
  .omit({
    id: true,
    userId: true,
    organizationId: true,
    lessonId: true,
    learnEnrollmentId: true,
    createdAt: true,
    updatedAt: true,
  })
  .superRefine((data, ctx) => {
    if (data.startedAt && data.endedAt && data.startedAt > data.endedAt) {
      ctx.addIssue({
        code: 'custom',
        message: 'Started at must be before ended at',
        path: ['startedAt'],
      })
    }

    if (data.gradedAt && data.startedAt && data.gradedAt < data.startedAt) {
      ctx.addIssue({
        code: 'custom',
        message: 'Graded at must be after started at',
        path: ['gradedAt'],
      })
    }

    if ((data.gradedAt && !data.gradedByUserId) || (!data.gradedAt && data.gradedByUserId)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Graded by user id is required when graded at is provided and vice versa',
        path: ['gradedByUserId', 'gradedAt'],
      })
    }
  })
export const SelectLearnProgressSchema = createSelectSchema(LearnProgress)

export type InsertLearnProgress = typeof LearnProgress.$inferInsert
export type UpdateLearnProgress = z.infer<typeof UpdateLearnProgressSchema>
export type SelectLearnProgress = typeof LearnProgress.$inferSelect
